import {
  CostsData,
  DealFormData,
  ExitData,
  FinancingData,
  InvestmentData,
  RevenueData,
} from "./DealEditor/types";

export interface SampleDeal {
  id: string;
  name: string;
  location: string;
  country: string;
  price: number;
  currency: string;
  rooms: number;
  gfaSqm: number;
  starRating: number;
  propertyType: "boutique-hotel" | "resort";
  facilities: string[];
  images: string[];
  description: string;
  investmentThesis: string;
  featured?: boolean;
}

export const initialFormData: DealFormData = {
  name: "",
  location: "",
  country: "",
  propertyType: "",
  starRating: 4,
  price: 0,
  currency: "EUR",
  gfaSqm: 0,
  rooms: 0,
  description: "",
  investmentThesis: "",
  images: "",
};

export const initialRevenueData: RevenueData = {
  roomRevenue: {
    monthlyADR: [100, 100, 100, 100, 150, 150, 200, 200, 100, 100, 100, 100],
    monthlyOccupancy: [55, 60, 65, 70, 75, 80, 85, 85, 80, 75, 65, 60],
  },
  // fbRevenue: {
  //   avgGuestsPerRoom: 1.8,
  //   breakfast: {
  //     capture: 80,
  //     avgCheck: 12,
  //     externalPerDay: 5,
  //     avgCheckExternal: 15,
  //   },
  //   lunch: {
  //     capture: 40,
  //     avgCheck: 18,
  //     externalPerDay: 15,
  //     avgCheckExternal: 22,
  //   },
  //   dinner: {
  //     capture: 60,
  //     avgCheck: 35,
  //     externalPerDay: 20,
  //     avgCheckExternal: 45,
  //   },
  //   bar: {
  //     capture: 45,
  //     avgCheck: 15,
  //     externalPerDay: 25,
  //     avgCheckExternal: 18,
  //   },
  // },
  spaRevenue: { treatmentsPerDay: 4, avgTreatmentPrice: 70 },
  otherRevenue: { model: "fixed", percentageOfRooms: 2, monthlyFixed: 2000 },
};

export const initialCostsData: CostsData = {
  directCosts: {
    roomsCommission: { value: 14, method: "percentage" },
    guestSupplies: { value: 8, method: "perRoomNight" },
    fbCogs: { value: 25, method: "percentage" },
    meetingEvents: { value: 2, method: "percentage" },
    wellness: { value: 1500, method: "fixed" },
    otherDirect: { value: 2000, method: "fixed" },
  },
  indirectCosts: {
    adminGeneral: { value: 4, method: "percentage" },
    techSubscriptions: { value: 1200, method: "fixed" },
    salesMarketing: { value: 3, method: "percentage" },
    maintenance: { value: 2, method: "percentage" },
    utilities: { value: 3, method: "percentage" },
  },
  otherCosts: { managementFee: { value: 3, method: "percentage" } },
  rampUp: {
    revenueScenario: "standard",
    costScenario: "standard",
    applyTo: {
      departmental: true,
      operating: true,
      undistributed: true,
      payroll: true,
    },
  },
  toplineGrowth: 3,
  inflation: 2,
  depreciation: 3,
};

export const initialInvestmentData: InvestmentData = {
  netPurchasePrice: 1500000,
  realEstateTransferTax: 0,
  dealCosts: 0,
  constructionCosts: 3000000,
  ffeOse: 187500,
  professionalFees: 318750,
  planningCharges: 0,
  developmentFee: 0,
  insuranceAdmin: 0,
  other: 0,
  preOpeningBudget: 0,
  contingencyPercent: 10,
};

export const initialFinancingData: FinancingData = {
  ltcRatio: 60,
  interestRate: 5,
  amortizationYears: 25,
  taxRate: 25,
};

export const initialExitData: ExitData = {
  holdPeriod: 10,
  exitCapRate: 6,
  sellingCosts: 2,
};

export const defaultKpis = {
  fnb: {
    check: {
      guest: { bar: 8, lunch: 22, dinner: 28, breakfast: 54 },
      external: { bar: 18, lunch: 20, dinner: 69, breakfast: 50 },
    },
    capture: { bar: 30, lunch: 20, dinner: 15, breakfast: 65 },
    external: { perDay: { bar: 25, lunch: 10, dinner: 20, breakfast: 5 } },
    avgGuests: 2.8,
  },
  exit: {
    strategy: "SALE",
    holdPeriodYears: 5,
    exitCapRate: 7,
    exitNOIMultiple: 12,
    sellingCostsPct: 2,
    valuationMethod: "capRate",
    sale: {
      exitYear: 3,
      exitCapRate: 6.5,
      exitNOIMultiple: 12,
      sellingCostsPct: 5,
      valuationMethod: "capRate",
    },
    refinance: {
      refinanceYear: 5,
      ltvAtRefinance: 70,
      refinanceCostsPct: 2,
    },
    estimatedReturns: {
      irrLevered: 0,
      irrUnlevered: 0,
      developmentProfit: 0,
    },
  },
  opex: {
    cogs: 30,
    rent: 120,
    mgmtFee: 3,
    insurance: 0.5,
    utilities: 3,
    otherDirect: 2000,
    propertyTaxes: 1,
    wellnessOther: 120000,
    meetingsEvents: 2,
    roomsCommission: 15,
    maintenanceOther: 2,
    adminGeneralOther: 4,
    techSubscriptions: 1200,
    salesMarketingOther: 3,
    guestSuppliesCleaning: 8,
  },
  ramp: {
    rampMonths: 12,
    openingMonth: 1,
    inflationRate: 5,
    revenueGrowthY2: 3,
    revenueGrowthY3Plus: 3,
    stabilizedOccupancy: 70,
  },
  // roomRev: {
  //   adr: Array(12).fill(100),
  //   occ: Array(12).fill(70),
  // },
  financing: {
    ltcPct: 60,
    amortYears: 10,
    dscrTarget: 1.25,
    loanAmount: 0,
    exitCapRate: 7,
    interestRate: 5,
    taxRateOnEBT: 30,
    ioPeriodYears: 0,
    loanTermYears: 20,
    equityRequired: 0,
    interestRatePct: 6,
    debtToEquityRatio: 60,
  },
  budget: {
    other: 0,
    devFee: 0,
    ffoeOse: 2160000,
    profFees: 2028800,
    dealCosts: 0,
    preopening: 0,
    transferTax: 0,
    construction: 20288000,
    planningFees: 0,
    contingencyPct: 10,
    insuranceAdmin: 0,
  },
  otherRev: {
    other: {
      mode: "pctRooms",
      value: 100,
    },
    wellness: {
      avgPrice: 1200,
      treatmentsPerDay: 4,
    },
  },

  rampSettings: {
    costRamp: [1.15, 1.1, 1, 1],
    revenueRamp: [0.85, 1, 1, 1],
    inflationPct: 5,
    applyCostRamp: {
      exclude: ["depreciation", "interest", "capex", "rent"],
      payroll: true,
      otherOpex: true,
      departmental: true,
      undistributed: true,
    },
    toplineGrowthPct: 3,
    depreciationPctOfCapex: 3,
  },
  investmentBudget: {
    ffeOse: 2160000,
    otherDev: 0,
    dealCosts: 0,
    grandTotal: 32974480,
    preOpening: 0,
    reTransferTax: 0,
    contingencyPct: 10,
    developmentFee: 0,
    insuranceAdmin: 0,
    planningCharges: 0,
    siteAcquisition: 5500000,
    netPurchasePrice: 5500000,
    otherDevSubtotal: 0,
    professionalFees: 2028800,
    constructionCosts: 20288000,
    contingencyAmount: 2997680,
    preOpeningSubtotal: 0,
    developmentSubtotal: 2028800,
    constructionSubtotal: 22448000,
  },
  notes: [],
  stars: 4,
  avgADR: 0,
  photos: [],
  address: "",

  roomMix: [],
  currency: "EUR",
  dealName: "",
  location: "",

  photoUrl: "",
  property: {
    facilities: [],
  },
  purchase: {
    price: 0,
  },
  amenities: {
    bar: false,
    gym: false,
    spa: false,
    pool: false,
    parking: false,
    meetings: false,
    restaurant: false,
    room_service: false,
  },
  avgRevPAR: 0,

  costPerSqm: 0,
  facilities: [],
  roomsTotal: 0,
  starRating: 4,
  costPerRoom: 0,
  fullAddress: "",
  roomRevenue: {},
  avgOccupancy: 0,
  dealLocation: "",
  otherRevenue: {
    monthly: {},
  },
  propertyType: "Boutique Hotel",
  purchasePrice: 0,
  operatingCosts: {
    lines: [],
  },
  totalInvestment: 0,
  budgetSelections: {},
  investmentThesis: "",
  totalRoomsRevenue: 0,
  normalizedFacilities: [],
};
